import type { CaptureSettings } from '../types';
import { applyImageTransformations } from './image-transformation';

export interface ApplyImageEnhancementsOptions {
  /** When true, skip rotation/zoom/perspective/tilt/yaw. Use when displaying already-captured images to avoid double-transforming and landmark misalignment. */
  skipGeometricTransforms?: boolean;
}

/**
 * Apply image enhancement filters to a canvas
 * Note: Transformations are applied AFTER enhancements to avoid quality loss
 */
export function applyImageEnhancements(
  canvas: HTMLCanvasElement,
  settings: CaptureSettings,
  options?: ApplyImageEnhancementsOptions
): HTMLCanvasElement {
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const skipGeometricTransforms = options?.skipGeometricTransforms === true;

  // Get image data
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // Apply Auto Brightness
  if (settings.autoBrightness) {
    applyAutoBrightness(data);
  }

  // Apply Contrast Boost
  if (settings.contrastBoost !== 0) {
    applyContrast(data, settings.contrastBoost);
  }

  // Apply Sharpening
  if (settings.sharpen) {
    applySharpen(ctx, imageData, canvas.width, canvas.height);
    // Sharpen already puts data back, so we need to get it again for transformations
    const sharpenedCanvas = document.createElement('canvas');
    sharpenedCanvas.width = canvas.width;
    sharpenedCanvas.height = canvas.height;
    const sharpenedCtx = sharpenedCanvas.getContext('2d');
    if (sharpenedCtx) {
      sharpenedCtx.drawImage(canvas, 0, 0);
      if (skipGeometricTransforms) return sharpenedCanvas;
      return applyImageTransformations(sharpenedCanvas, settings);
    }
    return canvas;
  }

  // Put the modified data back
  ctx.putImageData(imageData, 0, 0);

  if (skipGeometricTransforms) return canvas;
  return applyImageTransformations(canvas, settings);
}

/**
 * Auto-adjust brightness based on average luminance
 */
function applyAutoBrightness(data: Uint8ClampedArray): void {
  // Calculate average luminance
  let totalLuminance = 0;
  const pixelCount = data.length / 4;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    // Use perceived luminance formula
    totalLuminance += 0.299 * r + 0.587 * g + 0.114 * b;
  }

  const avgLuminance = totalLuminance / pixelCount;
  const targetLuminance = 128; // Middle gray
  const adjustment = targetLuminance - avgLuminance;

  // Only adjust if the image is noticeably dark or bright
  if (Math.abs(adjustment) > 20) {
    const factor = Math.min(Math.max(adjustment * 0.5, -50), 50); // Clamp adjustment
    
    for (let i = 0; i < data.length; i += 4) {
      data[i] = clamp(data[i] + factor);     // R
      data[i + 1] = clamp(data[i + 1] + factor); // G
      data[i + 2] = clamp(data[i + 2] + factor); // B
    }
  }
}

/**
 * Apply contrast adjustment
 * @param data - Image data array
 * @param amount - Contrast amount (-1 to 1)
 */
function applyContrast(data: Uint8ClampedArray, amount: number): void {
  // Convert -1 to 1 range to contrast factor
  // amount = 0 means no change, -1 is less contrast, 1 is more contrast
  const factor = (1 + amount) * (1 + amount); // Quadratic for more natural feel
  
  for (let i = 0; i < data.length; i += 4) {
    data[i] = clamp(factor * (data[i] - 128) + 128);     // R
    data[i + 1] = clamp(factor * (data[i + 1] - 128) + 128); // G
    data[i + 2] = clamp(factor * (data[i + 2] - 128) + 128); // B
  }
}

/**
 * Apply a simple sharpening filter using unsharp mask technique
 */
function applySharpen(
  ctx: CanvasRenderingContext2D, 
  imageData: ImageData, 
  width: number, 
  height: number
): void {
  const data = imageData.data;
  const output = new Uint8ClampedArray(data.length);
  
  // Simple 3x3 sharpening kernel
  const kernel = [
    0, -1, 0,
    -1, 5, -1,
    0, -1, 0
  ];
  
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      for (let c = 0; c < 3; c++) { // RGB channels only
        let sum = 0;
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = ((y + ky) * width + (x + kx)) * 4 + c;
            const kernelIdx = (ky + 1) * 3 + (kx + 1);
            sum += data[idx] * kernel[kernelIdx];
          }
        }
        const outIdx = (y * width + x) * 4 + c;
        output[outIdx] = clamp(sum);
      }
      // Copy alpha channel
      const alphaIdx = (y * width + x) * 4 + 3;
      output[alphaIdx] = data[alphaIdx];
    }
  }
  
  // Copy edges (not processed)
  for (let x = 0; x < width; x++) {
    for (let c = 0; c < 4; c++) {
      output[x * 4 + c] = data[x * 4 + c]; // Top row
      output[((height - 1) * width + x) * 4 + c] = data[((height - 1) * width + x) * 4 + c]; // Bottom row
    }
  }
  for (let y = 0; y < height; y++) {
    for (let c = 0; c < 4; c++) {
      output[(y * width) * 4 + c] = data[(y * width) * 4 + c]; // Left column
      output[(y * width + width - 1) * 4 + c] = data[(y * width + width - 1) * 4 + c]; // Right column
    }
  }
  
  // Put output back
  const outputImageData = new ImageData(output, width, height);
  ctx.putImageData(outputImageData, 0, 0);
}

/**
 * Clamp value to 0-255 range
 */
function clamp(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

